import { ExpandLess, ExpandMore } from "@mui/icons-material";
import * as MUI from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as BiIcons from "react-icons/bi";
import zookeeperIcon from "../../assets/svg/PawLogo.svg";
import zookeeperIconDarkBlue from "../../assets/svg/PawLogoDarkBlue.svg";
import { GlobalDataContext } from "../../contexts/GlobalData";
import {
  NotificationsContext,
  pendingNotificationKey,
} from "../../contexts/NotificationsProvider";
import { ThemeContext } from "../../contexts/ThemeContext";
import { VIEW_INBOX, VIEW_PENDING } from "../../enums/PermissionTitles";
import * as UrlPath from "../../enums/UrlPaths";
import usePrimaryColor from "../../hooks/usePrimaryColor";
import useUser from "../../hooks/useUser";
import LinkTo from "../wrappers/LinkTo";
import Bottom from "./components/Bottom";
import Header from "./components/Header";
import { useGetCollapsedSidebarIcons } from "./hooks/useGetCollapsedSidebarData";
import { useIsHomePage } from "./hooks/useIsHomePage";

const DRAWER_WIDTH = { xs: "70%", sm: 300 };

// ---------------------------------------------------------------------------
// NavSidebarLink — a single nav item row
// ---------------------------------------------------------------------------
export function NavSidebarLink({ item, index, onClose }) {
  const { t } = useTranslation();
  const color = usePrimaryColor();
  const queryClient = useQueryClient();
  const { notifications, newInbox } = React.useContext(NotificationsContext);
  const { currentClinic } = useUser();

  const pendingCount = queryClient.getQueryData([
    pendingNotificationKey + currentClinic.id,
  ]);

  const isPending = item.path === UrlPath.PENDING_APPOINTMENTS;
  const isInbox = item.path === UrlPath.INBOX;
  return (
    <LinkTo to={item.path}>
      <MUI.ListItem key={index} disablePadding onClick={onClose}>
        <MUI.ListItemButton>
          <MUI.ListItemIcon sx={{ color }}>{item.icon}</MUI.ListItemIcon>

          <MUI.ListItemText primary={t(item.title)} />

          {isPending && (
            <MUI.Badge
              invisible={!pendingCount}
              badgeContent={notifications}
              color="error"
              sx={{ position: "relative", right: 20 }}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
            />
          )}

          {isInbox && (
            <MUI.Badge
              badgeContent={newInbox}
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              color="error"
              invisible={newInbox < 1}
              sx={{ mr: 2 }}
            />
          )}
        </MUI.ListItemButton>
      </MUI.ListItem>
    </LinkTo>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleLink — a group of nav items that expand/collapse
// ---------------------------------------------------------------------------
export function CollapsibleLink({ item, onClose }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const color = usePrimaryColor();

  return (
    <Fragment>
      <MUI.ListItemButton onClick={() => setOpen((prev) => !prev)}>
        <MUI.ListItemIcon sx={{ color }}>{item[0].icon}</MUI.ListItemIcon>
        <MUI.ListItemText primary={t(item[0].name)} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </MUI.ListItemButton>

      <MUI.Collapse in={open} timeout="auto" unmountOnExit>
        <MUI.List component="div" disablePadding sx={{ pl: 3, py: 1 }}>
          {item.map((subItem, subIndex) => (
            <NavSidebarLink
              key={subItem.path}
              item={subItem}
              index={subIndex}
              onClose={onClose}
            />
          ))}
        </MUI.List>
      </MUI.Collapse>
    </Fragment>
  );
}

// ---------------------------------------------------------------------------
// NavBar
// ---------------------------------------------------------------------------
export default function NavBar() {
  const { notifications, newInbox } = React.useContext(NotificationsContext);
  const { roles, currentClinic } = useUser();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { mode } = React.useContext(ThemeContext);
  const queryClient = useQueryClient();
  const {t} = useTranslation();
  const color = usePrimaryColor();
  const isInHomePage = useIsHomePage();
  const collapsedIcons = useGetCollapsedSidebarIcons();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const adminDividerShown = useRef(false);

  const pendingCount = queryClient.getQueryData([
    pendingNotificationKey + currentClinic.id,
  ]);

  const icon = mode === "dark" ? zookeeperIcon : zookeeperIconDarkBlue;

  const openSidebar = () => setSidebarOpen(true);
  const closeSidebar = () => setSidebarOpen(false);

  const hasNotifications =
    (pendingCount > 0 &&
      roles.adminOrEmployee &&
      isPermissionAllowed(VIEW_PENDING)) ||
    (newInbox > 0 && isPermissionAllowed(VIEW_INBOX));

  const isAdminOnlyPath = (path) =>
    roles.adminOrEmployee && [UrlPath.CLINIC_USERS].includes(path);

  return (
    <Fragment>
      {/* ── Top AppBar ── */}
      <MUI.AppBar
        position="fixed"
        color={isInHomePage ? "transparent" : ""}
        sx={{
          boxShadow: "none",
          height: 60,
          bgcolor: isInHomePage ? "transparent" : "",
        }}
      >
        <MUI.Toolbar
          sx={{ display: "flex", alignItems: "center", height: "auto" }}
        >
          <MUI.Badge color="error" variant="dot" invisible={!hasNotifications}>
            <MUI.IconButton
              title={t("Menu")}
              sx={{ bgcolor: isInHomePage ? color : "" }}
              onClick={openSidebar}
              data-testid="nav.sidebar.trigger"
            >
              <BiIcons.BiMenu color="white" />
            </MUI.IconButton>
          </MUI.Badge>
          <Header />
        </MUI.Toolbar>
      </MUI.AppBar>

      {/* ── Sidebar Drawer ── */}
      <MUI.Drawer
        open={sidebarOpen}
        onClose={closeSidebar}
        className="Mui-nav-bar"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {/* Logo */}
        <MUI.Stack
          onClick={closeSidebar}
          direction="row"
          alignContent="center"
          justifyContent="center"
          padding={1}
          minHeight={70}
          className="Mui-nav-bar"
        >
          <MUI.IconButton
            component="a"
            href={UrlPath.HOME}
            sx={{ ":hover": { background: "transparent" } }}
            data-testid="nav.sidebar.logo"
          >
            <img src={icon} width="30px" alt="paw_icon" />
          </MUI.IconButton>
        </MUI.Stack>

        <MUI.Divider sx={{ mb: 2 }} />

        <MUI.Stack justifyContent="space-between" height="100%">
          {/* Nav items */}
          <MUI.List className="Mui-nav-bar">
            {collapsedIcons.map((item, index) => {
              const showAboutUsDivider =
                item.path === UrlPath.ABOUT_US && !roles.user;
              const showAdminDivider = isAdminOnlyPath(item.path);

              if (showAdminDivider) {
                adminDividerShown.current = true;
              }

              return (
                <Fragment key={Array.isArray(item) ? index : item.path}>
                  {showAboutUsDivider && <MUI.Divider sx={{ mt: 2, mb: 2 }} />}
                  {showAdminDivider && <MUI.Divider sx={{ mt: 2, mb: 2 }} />}

                  {Array.isArray(item) ? (
                    <CollapsibleLink item={item} onClose={closeSidebar} />
                  ) : (
                    <NavSidebarLink
                      item={item}
                      index={index}
                      onClose={closeSidebar}
                    />
                  )}
                </Fragment>
              );
            })}
          </MUI.List>

          <Bottom />
        </MUI.Stack>
      </MUI.Drawer>
    </Fragment>
  );
}
